/**
 * Non-human identity census — Zod schemas.
 *
 * Mirrors backend/internal/ops/census (census.go, projector.go). Tenant-scoped:
 *   /api/v1/tenants/{tenant_id}/non-human-identities
 *
 * The census is the roster the "Non-human identities" PLACE is named after. It
 * spans five tables, because three of the six principal kinds are not `agents`
 * rows: an automation account is an admin_users row, an auditor token is its own
 * table, and a directory connector reaches the roster from TWO (a SCIM token, or
 * an AD-inbound directory holding an OAuth client credential).
 * docs/product-areas.md makes the top-level place conditional on that roster
 * being complete.
 */

import { z } from "zod";
import { principalRefSchema } from "@pureid/auth-core";

// ── Principal kind: what the thing IS in the identity model ────────────────
// Immutable and constraint-bearing on the backend. Distinct from category below;
// the two must never be collapsed into one control.
export const censusKindSchema = z.enum([
  "agent",
  "workload_identity",
  "api_key",
  "automation_account",
  "directory_connector",
  "auditor_token",
  // A CLIENT-SIDE DEGRADATION MARKER, never sent by the server.
  //
  // The backend passes agents.kind through verbatim, so widening
  // chk_agents_kind ships a value this enum has never seen — and a strict enum
  // inside censusResponseSchema turns that into a THROWN PARSE and a blank
  // page. Blanking the front door of a top-level place because one row grew a
  // new word is a worse failure than rendering that row as unrecognized.
  //
  // It is a real member rather than a `.catch()` into an existing one because
  // the census's own doctrine is to state what it does not know instead of
  // guessing: silently relabelling a new kind as "agent" would be the same
  // coverage-flattering substitution §25 forbids on the server.
  // An Edge deployment the tenant operates: the machine that decides governed
  // calls in the tenant's name, authenticating to the control plane by mTLS.
  // The sixth source (2026-09-05).
  "enforcement_point",
  "unrecognized",
]);
export type CensusKind = z.infer<typeof censusKindSchema>;

// ── Census category: what the thing is for someone COUNTING the estate ─────
// The vocabulary the kind chips and the posture mix bar are drawn in. Four kinds
// map here by construction; a workload identity resolves from the trusted issuer
// it is bound to, and "unclassified" is a real segment rather than a gap.
export const censusCategorySchema = z.enum([
  "ai_agent",
  "cloud_workload",
  "pipeline",
  "service_account",
  "api_key",
  "directory_connector",
  "auditor",
  "enforcement_point",
  "unclassified",
]);
export type CensusCategory = z.infer<typeof censusCategorySchema>;

// ── Derived state: the ONE grammar the roster speaks ───────────────────────
// Never a stored status column. A credential past its expiry reads "expired"
// even where its row still says active, because the janitor that writes the
// terminal status runs on a sweep while the auth path refuses it immediately.
// "untrusted_issuer" is the one that is not about the row at all: the identity
// is live, and the trusted issuer it is bound to is disabled or gone, so nothing
// behind that issuer can authenticate. It is listed, never dropped — a
// credential the tenant still holds belongs on the roster — and it is not
// counted as live.
export const censusStateSchema = z.enum([
  "active",
  "pending",
  "suspended",
  "untrusted_issuer",
  "expired",
  "revoked",
  // Client-side degradation marker; see censusKindSchema. Deliberately NOT
  // caught into "active": an unrecognized state must never be counted as one
  // that can act, which is the same direction StateFor takes on the server.
  "unrecognized",
]);
export type CensusState = z.infer<typeof censusStateSchema>;

// A directory connector reaches the roster from TWO sources, because this
// product authenticates connectors two ways: a SCIM bearer token, and an
// AD-inbound directory whose on-prem agent presents an OAuth client credential.
// Same kind and category, different auth model — and each is managed on its own
// surface, which is what `source` is carried for.
export const censusSourceSchema = z.enum([
  "agents",
  "automation_accounts",
  "scim_tokens",
  "auditor_tokens",
  "directories",
  "edges",
  // Client-side degradation marker; see censusKindSchema. A row from a source
  // this build does not know about still renders — it just has no deep link,
  // which is the honest outcome.
  "unrecognized",
]);
export type CensusSource = z.infer<typeof censusSourceSchema>;

// The vocabularies are CAUGHT at the row level, not at the enum level.
//
// The enums stay strict so `search` params and internal code keep exhaustive
// types; only the wire shape degrades. A single unknown value from a widened
// backend enum then costs one row's label, not the whole page — the parse
// throwing would leave an operator staring at an error boundary with no way to
// tell whether their estate is empty or the client is out of date.
//
// category catches to "unclassified", which is a REAL member meaning exactly
// "nobody has classified this" rather than an invented marker.
// ── Proof of identity: how strongly this identity proves it is itself ──────
// The surface word is "Proof", never "binding grade" — the mock states it as a
// rule and CLAUDE.md repeats it: no internal jargon reaches a product surface.
//
// The level NAMES THE WEAKEST WAY IN, never the strongest available, because the
// weakest is the one an attacker uses. Derived server-side per source
// (backend/internal/ops/census/derive.go) and mirrored by deriveProofLevel in
// @pureid/auth-core, which the agent registry renders one rail-click away.
export const censusProofSchema = z.enum([
  "per_call",
  "attested",
  "bearer",
  // A CLIENT-SIDE DEGRADATION MARKER, never sent by the server — same reasoning
  // as censusKindSchema. A fourth level would otherwise throw the whole parse
  // and blank the front door of a top-level place.
  //
  // It is a real member rather than a `.catch("bearer")` even though bearer is
  // the SAFE-looking answer, because quietly relabelling an unknown level as the
  // weakest one is still a guess presented as a measurement — and it would move
  // the mix bar. Saying "unrecognized" is the census's own doctrine.
  "unrecognized",
]);
export type CensusProof = z.infer<typeof censusProofSchema>;

export const censusRowSchema = z.object({
  id: z.string(),
  source: censusSourceSchema.catch("unrecognized"),
  kind: censusKindSchema.catch("unrecognized"),
  category: censusCategorySchema.catch("unclassified"),
  name: z.string(),
  state: censusStateSchema.catch("unrecognized"),
  // The trusted issuer a workload identity is bound to, and the reason its
  // category is what it is. Absent for every other kind. Carried so an
  // Unclassified row can link straight to the issuer that would fix it.
  issuer: z.string().optional(),
  // Absent means "never expires on its own" — a finding, not a blank.
  expires_at: z.string().optional(),
  // Absent means never used OR not tracked for this kind. The UI must not render
  // those two as the same sentence.
  last_used_at: z.string().optional(),
  // The dormancy finding: a LIVE row not used inside the catalog's window.
  // Absent when not dormant, or when no use was ever recorded (that is the
  // "Not recorded" sentence, a different finding).
  dormant: z.boolean().optional(),
  created_at: z.string(),
  // Always present — every source can answer it, which is what makes it a column
  // the roster can carry. See censusProofSchema.
  proof: censusProofSchema.catch("unrecognized"),
  // What does the proving ("DPoP", "mTLS", "federated"). Absent for every bearer
  // row, deliberately: naming a mechanism a bearer identity ALSO holds would let
  // the chip imply that presenting the pasted secret is not enough. It is.
  proof_mechanism: z.string().optional(),
  // The accountable human, resolved for display. ABSENT ON A TRANSIENT RESOLVE
  // FAULT (the server omits it rather than claiming "deleted"), which is why
  // owner_id is carried separately — see below.
  owner: principalRefSchema.optional(),
  // The raw owner id, present whenever the row HAS an owner regardless of whether
  // it resolved. It is what separates "nobody is accountable" from "we could not
  // read who is accountable", two facts that must never render the same way on an
  // accountability surface.
  owner_id: z.string().optional(),
});
export type CensusRow = z.infer<typeof censusRowSchema>;

export const censusFacetSchema = z.object({
  value: z.string(),
  count: z.number().int().nonnegative(),
});
export type CensusFacet = z.infer<typeof censusFacetSchema>;

export const censusSourceStatusSchema = z.object({
  // CAUGHT, like the row-level fields. This array is present in EVERY response
  // and carries one entry per source, so a strict enum here made a sixth backend
  // source throw the whole parse — the exact failure the row-level catches fix,
  // left open on the one field that is never absent. A source this build does not
  // know still has a count worth showing; only its label degrades.
  source: censusSourceSchema.catch("unrecognized"),
  visible: z.boolean(),
  count: z.number().int().nonnegative(),
  // What the viewer would need to hold to see these rows. Present only when
  // visible is false, so the UI can name the access to ask for instead of
  // rendering an unexplained gap.
  required_permission: z.string().optional(),
});
export type CensusSourceStatus = z.infer<typeof censusSourceStatusSchema>;

export const censusResponseSchema = z.object({
  rows: z.array(censusRowSchema),
  // Rows matching the current filter, across visible sources.
  total: z.number().int().nonnegative(),
  // THE DENOMINATOR: every source, filtered or not, visible or not. It does not
  // move with the viewer's permissions and it does not move when a filter is
  // applied — both properties are asserted by real-DB tests on the backend.
  census: z.number().int().nonnegative(),
  live: z.number().int().nonnegative(),
  // How many have NO accountable human, across the whole estate. The census's own
  // headline finding, and a COUNT rather than a percentage (§24's gate stands).
  unowned: z.number().int().nonnegative(),
  // Live device identities (registry kind `self` — a person's own devices, ruling
  // R4). Deliberately OUTSIDE the census denominator: they are people's devices,
  // surfaced under People, and never a metered non-human. The count travels so the
  // roster can state the reconciliation instead of leaving an auditor to find the
  // gap by counting credentials.
  devices: z.number().int().nonnegative(),
  // Device identities whose person is no longer active — the residue finding R4
  // forbids hiding. Excluded from `unowned` (that count feeds the same queries the
  // billing fence guards), so this is its only surface.
  device_orphans: z.number().int().nonnegative(),
  categories: z.array(censusFacetSchema),
  kinds: z.array(censusFacetSchema),
  states: z.array(censusFacetSchema),
  // The proof mix — the roster's one honest headline proportion, counted against
  // the census denominator. NOT a coverage percentage: §24 gates any percentage
  // in this area on two open denominator holes, and "how much of my estate can be
  // impersonated by a stolen secret" depends on neither.
  proofs: z.array(censusFacetSchema),
  sources: z.array(censusSourceStatusSchema),
  // True when at least one source's rows were withheld from this viewer. The
  // console MUST surface it: a roster quietly missing a source is undetectable
  // from the rows themselves, and that is the §24 failure.
  partial: z.boolean(),
  read_at: z.string(),
});
export type CensusResponse = z.infer<typeof censusResponseSchema>;

// ── List search (the URL is the source of truth) ───────────────────────────
// Sortable columns, mirroring census.SortField on the server. Sorting is applied
// SERVER-SIDE over the whole filtered set: a client-side sort would order the
// visible page and present it as if it had ordered the estate, so "what expires
// first" would answer with the earliest expiry on THIS PAGE.
export const censusSortSchema = z.enum([
  "created_at",
  "name",
  "state",
  "expires_at",
  "last_used_at",
]);
export type CensusSort = z.infer<typeof censusSortSchema>;

export const censusOrderSchema = z.enum(["asc", "desc"]);
export type CensusOrder = z.infer<typeof censusOrderSchema>;

export const censusSearchSchema = z.object({
  category: censusCategorySchema.optional(),
  kind: censusKindSchema.optional(),
  state: censusStateSchema.optional(),
  q: z.string().optional(),
  sort: censusSortSchema.optional(),
  order: censusOrderSchema.optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type CensusSearch = z.infer<typeof censusSearchSchema>;
